import { Component, OnInit } from '@angular/core';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface IdValue {
  id: number;
  text: string;
}

interface TabelaValor {
  idTabelaFin: number;
  idValor: number;
  itemMicroservico: string;
  descricaoMicroservico: string;
  nomeFornecedor: string;
  inicioVigencia: string;
  fimVigencia: string;
  valor: number;
  observacoes: string;
  isAtivo: boolean;
}

@Component({
  selector: 'app-visualizar-tabela-valores',
  templateUrl: './visualizar-tabela-valores.component.html',
  styleUrls: ['./visualizar-tabela-valores.component.scss']
})
export class VisualizarTabelaValoresComponent implements OnInit {

  tabelasValores: TabelaValor[] = [];
  tabelasValoresPaginadas: TabelaValor[] = [];
  loading: boolean = false;
  errorMessage: string = '';

  fornecedores: IdValue[] = [];
  filtroMicroservicoTexto: string = '';
  idFornecedorFiltro: string = '';
  paginaAtual: number = 1;
  itensPorPagina: number = 10;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService
  ) { }

  ngOnInit(): void {
    this.carregarFiltros();
    this.carregarTabelasValores();
  }

  carregarFiltros(): void {
    const url = API + 'empresa/TabelaValores/ConsultarFiltros';
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        const data = response?.data || {};
        const fornecedores = Array.isArray(data.fornecedores) ? data.fornecedores : [];

        this.fornecedores = fornecedores
          .map((x: any) => ({ id: Number(x.id || 0), text: this.toText(x.text || x.value) }))
          .filter((x: IdValue) => x.id > 0 && !!x.text);
      }
    });
  }

  carregarTabelasValores(): void {
    this.loading = true;
    this.errorMessage = '';

    const query = this.montarQueryFiltros();
    const url = API + `empresa/TabelaValores/ConsultarTabelaValores${query}`;

    this.util.showLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();

        const lista = response?.data && Array.isArray(response.data) ? response.data : [];
        this.tabelasValores = lista.map((item: any) => ({
          idTabelaFin: Number(item.idTabelaFin || 0),
          idValor: Number(item.idValor || 0),
          itemMicroservico: this.toText(item.itemMicroservico),
          descricaoMicroservico: this.toText(item.descricaoMicroservico),
          nomeFornecedor: this.toText(item.nomeFornecedor),
          inicioVigencia: this.toText(item.inicioVigencia),
          fimVigencia: this.toText(item.fimVigencia),
          valor: Number(item.valor || 0),
          observacoes: this.toText(item.observacoes),
          isAtivo: !!item.isAtivo
        }));
        this.paginaAtual = 1;
        this.atualizarPaginacao();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.errorMessage = 'Erro ao carregar tabela de valores. Tente novamente.';
        this.tabelasValores = [];
        this.tabelasValoresPaginadas = [];
      }
    });
  }

  buscar(): void {
    this.carregarTabelasValores();
  }

  limparFiltros(): void {
    this.filtroMicroservicoTexto = '';
    this.idFornecedorFiltro = '';
    this.carregarTabelasValores();
  }

  alterarStatus(item: TabelaValor): void {
    const novoStatus = !item.isAtivo;
    const payload = {
      idTabelaValor: String(item.idValor),
      isAtivar: novoStatus
    };

    const url = API + 'empresa/TabelaValores/ControlarStatusAtivo';
    this.util.showLoading();
    this.ajaxService.post(url, payload).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível atualizar o status.');
          return;
        }

        item.isAtivo = novoStatus;
        this.util.toasterShowSuccess('Sucesso', novoStatus ? 'Valor ativado com sucesso!' : 'Valor inativado com sucesso!');
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao atualizar o status do valor.');
      }
    });
  }

  getStatusClass(ativo: boolean): string {
    return ativo ? 'badge bg-success' : 'badge bg-secondary';
  }

  formatarVigencia(item: TabelaValor): string {
    const inicio = this.toText(item.inicioVigencia);
    const fim = this.toText(item.fimVigencia);
    if (!inicio && !fim) return '-';
    if (!inicio) return fim;
    if (!fim) return inicio;
    return `${inicio} à ${fim}`;
  }

  private montarQueryFiltros(): string {
    const queryParts: string[] = [];
    if (this.filtroMicroservicoTexto) queryParts.push(`microservicoTexto=${encodeURIComponent(this.filtroMicroservicoTexto)}`);
    if (this.idFornecedorFiltro) queryParts.push(`idFornecedor=${encodeURIComponent(this.idFornecedorFiltro)}`);
    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }

  private toText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  formatarMoeda(value: number): string {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  get totalPaginas(): number {
    if (this.itensPorPagina <= 0) return 1;
    return Math.max(1, Math.ceil(this.tabelasValores.length / this.itensPorPagina));
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  paginaAnterior(): void {
    if (this.paginaAtual <= 1) return;
    this.paginaAtual--;
    this.atualizarPaginacao();
  }

  proximaPagina(): void {
    if (this.paginaAtual >= this.totalPaginas) return;
    this.paginaAtual++;
    this.atualizarPaginacao();
  }

  private atualizarPaginacao(): void {
    if (!this.tabelasValores.length) {
      this.tabelasValoresPaginadas = [];
      return;
    }

    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.tabelasValoresPaginadas = this.tabelasValores.slice(inicio, fim);
  }
}